import React, { useState, useEffect, useRef } from "react";
import { FormGroup, FormControl, ControlLabel, Button } from "react-bootstrap";
import { fetchUserAttributes, updateUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { uploadData, getUrl, remove } from 'aws-amplify/storage';
import "./Profile.css";

export default function Profile(props) {
  const [userEmail, setUserEmail] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    loadUserProfile();
  }, []);

  async function loadUserProfile() {
    try {
      const attributes = await fetchUserAttributes();
      setUserEmail(attributes.email || "");
      
      // Load profile photo if exists
      if (attributes.picture) {
        try {
          const result = await getUrl({
            path: attributes.picture
          });
          setPhotoUrl(result.url.toString());
        } catch (e) {
          console.log('No profile photo found');
        }
      }
    } catch (e) {
      console.error('Error loading profile:', e);
    }
  }

  async function handlePhotoChange(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      return;
    }

    setIsUploading(true);

    try {
      // Get user's identity ID
      const session = await fetchAuthSession();
      const identityId = session.identityId;
      
      if (!identityId) {
        throw new Error('Could not get user identity');
      }

      // Delete old photo if exists
      const attributes = await fetchUserAttributes();
      if (attributes.picture) {
        try {
          await remove({ path: attributes.picture });
        } catch (e) {
          console.log('No old photo to delete');
        }
      }

      // Upload new photo with explicit private path
      const fileName = `private/${identityId}/profile-photo-${Date.now()}.jpg`;
      await uploadData({
        path: fileName,
        data: file,
        options: {
          contentType: file.type
        }
      }).result;

      // Update user attributes with photo path
      await updateUserAttributes({
        userAttributes: {
          picture: fileName
        }
      });

      // Get URL for display
      const urlResult = await getUrl({
        path: fileName
      });
      setPhotoUrl(urlResult.url.toString());

      alert('Profile photo updated successfully!');
    } catch (e) {
      console.error('Error uploading photo:', e);
      alert('Failed to upload photo: ' + e.message);
    } finally {
      setIsUploading(false);
    }
  }

  async function handleRemovePhoto() {
    if (!window.confirm('Are you sure you want to remove your profile photo?')) {
      return;
    }

    setIsLoading(true);

    try {
      const attributes = await fetchUserAttributes();
      if (attributes.picture) {
        // Delete from S3
        await remove({ path: attributes.picture });

        // Update user attributes
        await updateUserAttributes({
          userAttributes: {
            picture: ''
          }
        });

        setPhotoUrl('');
        alert('Profile photo removed successfully!');
      }
    } catch (e) {
      console.error('Error removing photo:', e);
      alert('Failed to remove photo: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="Profile">
      <h2>Profile Settings</h2>
      
      <FormGroup>
        <ControlLabel>Email</ControlLabel>
        <FormControl
          type="text"
          value={userEmail}
          disabled
        />
      </FormGroup>

      <FormGroup>
        <ControlLabel>Profile Photo</ControlLabel>
        <div className="profile-photo-section">
          {photoUrl ? (
            <div className="photo-preview">
              <img src={photoUrl} alt="Profile" />
              <Button
                bsStyle="danger"
                bsSize="small"
                onClick={handleRemovePhoto}
                disabled={isLoading}
              >
                Remove Photo
              </Button>
            </div>
          ) : (
            <div className="no-photo">
              <div className="placeholder-avatar">
                {userEmail ? userEmail.charAt(0).toUpperCase() : '?'}
              </div>
              <p>No profile photo</p>
            </div>
          )}
          
          <FormControl
            type="file"
            accept="image/*"
            inputRef={ref => fileInput.current = ref}
            onChange={handlePhotoChange}
            disabled={isUploading}
          />
          
          <p className="help-text">
            Upload a profile photo (max 2MB, JPG/PNG/GIF)
          </p>
        </div>
      </FormGroup>

      {isUploading && <p className="uploading-text">Uploading photo...</p>}
    </div>
  );
}
